import React, {useState} from 'react';
import Helmet from 'react-helmet'
import {auth, firestore, functions} from '../../Firebase'
import {Spinner, Message, displaySpinner, displayMessage} from '../ModalComponents/Spinner.js'

const ACCOUNT_DELETED_MESSAGE = 'Your account has been deleted. Please contact support.';
const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password.';
const GENERIC_SIGNIN_ERROR_MESSAGE = 'Unable to sign in. Please try again.';


const ROLE_MESSAGES = {
  superAdmin: "Super Admin accounts cannot access support on this website. Please use a customer account.",
  admin: "Admin accounts cannot access support on this website. Please use a customer account.",
  logCompanyAdmin: "Logistic Company Admin accounts cannot access support on this website. Please use a customer account.",
  lcDriver: "LC driver accounts cannot access support on this website. Please use a customer account.",
  trippieDriver: "Trippie driver accounts cannot access support on this website. Please use a customer account.",
};

/** Returns true if user is a Logistic Company Admin (in user_admin with company). Used when token claims are not yet set. */
async function isLogCompanyAdminFromFirestore(uid) {
  try {
    const doc = await firestore.collection('user_admin').doc(uid).get();
    return doc.exists && doc.data() && !!doc.data().company;
  } catch {
    return false;
  }
}

/** Returns 'lcDriver' | 'trippieDriver' | null */
async function getDriverRole(uid) {
  const doc = await firestore.collection('user_private').doc(uid).get();
  if (!doc.exists || !doc.data() || doc.data().hasDriverProfile !== true) return null;
  return doc.data().companyId ? 'lcDriver' : 'trippieDriver';
}

function signInWithEmailAndPasswordHandler(email, password){
  displaySpinner(true);
  auth.signInWithEmailAndPassword(email, password).then(async (userCred) => {
    if (userCred && !userCred.user.emailVerified) {
      auth.signOut();
      displaySpinner(false);
      displayMessage(true, false, "The email associated with this account has not been verified!");
      return;
    }
    try {
      const tokenResult = await userCred.user.getIdTokenResult();
      const claims = tokenResult.claims || {};
      if (claims.superAdmin) {
        await auth.signOut();
        displaySpinner(false);
        displayMessage(true, false, ROLE_MESSAGES.superAdmin);
        return;
      }
      // Logistic Company Admin only: user must be in user_admin with company (not token claims - Company Customers may have companyId)
      if (await isLogCompanyAdminFromFirestore(userCred.user.uid)) {
        await auth.signOut();
        displaySpinner(false);
        displayMessage(true, false, ROLE_MESSAGES.logCompanyAdmin);
        return;
      }
      if (claims.admin) {
        await auth.signOut();
        displaySpinner(false);
        displayMessage(true, false, ROLE_MESSAGES.admin);
        return;
      }
      const driverRole = await getDriverRole(userCred.user.uid);
      if (driverRole) {
        await auth.signOut();
        displaySpinner(false);
        displayMessage(true, false, ROLE_MESSAGES[driverRole]);
        return;
      }
    } catch (err) {
      displaySpinner(false);
      displayMessage(true, false, err.message || "Unable to verify account.");
      return;
    }
    displaySpinner(false);
  }).catch(async (error) => {
    displaySpinner(false);
    const code = error && error.code;
    const isUnsafeAuthError =
      code === 'auth/invalid-credential' ||
      code === 'auth/user-not-found' ||
      code === 'auth/internal' ||
      !code;
    if (isUnsafeAuthError && email) {
      try {
        const checkAccountDeleted = functions.httpsCallable('checkAccountDeleted');
        const result = await checkAccountDeleted({ email: email.trim().toLowerCase() });
        if (result.data && result.data.deleted === true) {
          displayMessage(true, false, ACCOUNT_DELETED_MESSAGE);
          return;
        }
      } catch (_) {}
      displayMessage(true, false, INVALID_CREDENTIALS_MESSAGE);
      return;
    }
    displayMessage(true, false, GENERIC_SIGNIN_ERROR_MESSAGE);
  });
}

export default function Login(){

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return(
    <div className="login-container">
      <Helmet>
        <title>Trippie Air - Login</title>
        <meta name="description" content="Trippie Air" />
      </Helmet>
      <Spinner/>
      <Message/>
      <div className="login-box">
        <div className="login-form">
          <div className="login-message">
            Sign in to your existing Trippie Air account access support
          </div>
          <input
            required={true}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            />
          <input
            required={true}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <div className="login-button">
            <button className="standard-button"onClick={() => signInWithEmailAndPasswordHandler(email, password)}>Login</button>
          </div>
        </div>
      </div>
    </div>
  );
}
